/**
 * Tool Registry
 *
 * Central registry for managing tool providers.
 * Uses the Strategy pattern to allow multiple providers to be registered and used.
 *
 * Usage:
 *   // Register providers
 *   toolRegistry.registerProvider(builtinToolProvider)
 *
 *   // Get tools from all providers
 *   const tools = await toolRegistry.getAllTools()
 *
 *   // Get tools from specific provider
 *   const provider = toolRegistry.getProvider('builtin')
 *   const tools = await provider.listTools()
 */

import type {
  Tool,
  ToolProvider,
  ToolProviderType,
  ListToolsOptions,
} from './types'

class ToolRegistry {
  private providers: Map<ToolProviderType, ToolProvider> = new Map()
  private initialized = false

  /**
   * Register a tool provider
   */
  registerProvider(provider: ToolProvider): void {
    this.providers.set(provider.name, provider)
  }

  /**
   * Unregister a tool provider
   */
  unregisterProvider(name: ToolProviderType): void {
    this.providers.delete(name)
  }

  /**
   * Get a specific provider by name
   */
  getProvider(name: ToolProviderType): ToolProvider | undefined {
    return this.providers.get(name)
  }

  /**
   * Get all registered providers
   */
  getProviders(): ToolProvider[] {
    return Array.from(this.providers.values())
  }

  /**
   * Check if a provider is registered
   */
  hasProvider(name: ToolProviderType): boolean {
    return this.providers.has(name)
  }

  /**
   * Initialize all registered providers
   */
  async initializeAll(): Promise<void> {
    if (this.initialized) {
      return
    }

    const initPromises = Array.from(this.providers.values()).map((p) =>
      p.initialize().catch((err) => {
        console.error(`Failed to initialize provider ${p.name}:`, err)
      })
    )

    await Promise.all(initPromises)
    this.initialized = true
  }

  /**
   * Get all tools from all registered providers
   */
  async getAllTools(options?: ListToolsOptions): Promise<Tool[]> {
    await this.initializeAll()

    const providerPromises = Array.from(this.providers.values())
      .filter((p) => !options?.provider || p.name === options.provider)
      .map((p) => p.listTools())

    const toolArrays = await Promise.all(providerPromises)
    let tools = toolArrays.flat()

    // Apply filters
    if (options?.category) {
      tools = tools.filter((t) => t.category === options.category)
    }

    if (options?.enabledOnly) {
      tools = tools.filter((t) => t.enabled)
    }

    if (options?.search) {
      const query = options.search.toLowerCase()
      tools = tools.filter(
        (t) =>
          t.name.toLowerCase().includes(query) ||
          t.description.toLowerCase().includes(query)
      )
    }

    return tools
  }

  /**
   * Search tools across all providers
   */
  async searchTools(query: string): Promise<Tool[]> {
    await this.initializeAll()

    const searchPromises = Array.from(this.providers.values()).map((p) =>
      p.searchTools(query)
    )

    const resultArrays = await Promise.all(searchPromises)
    return resultArrays.flat()
  }

  /**
   * Get a tool by ID from any provider
   */
  async getToolById(id: string): Promise<Tool | null> {
    await this.initializeAll()

    for (const provider of this.providers.values()) {
      const tool = await provider.getToolById(id)
      if (tool) {
        return tool
      }
    }

    return null
  }

  /**
   * Get tools by multiple IDs
   */
  async getToolsByIds(ids: string[]): Promise<Tool[]> {
    const tools: Tool[] = []

    for (const id of ids) {
      const tool = await this.getToolById(id)
      if (tool) {
        tools.push(tool)
      }
    }

    return tools
  }

  /**
   * Get all unique categories across all providers
   */
  async getAllCategories(): Promise<string[]> {
    await this.initializeAll()

    const categoryPromises = Array.from(this.providers.values()).map((p) =>
      p.getCategories()
    )

    const categoryArrays = await Promise.all(categoryPromises)
    const allCategories = categoryArrays.flat()

    return Array.from(new Set(allCategories))
  }

  /**
   * Validate tool configuration
   */
  async validateToolConfig(toolId: string, config: unknown): Promise<boolean> {
    const tool = await this.getToolById(toolId)
    if (!tool) {
      return false
    }

    const provider = this.providers.get(tool.provider)
    if (!provider) {
      return false
    }

    return provider.validateConfig(toolId, config)
  }

  /**
   * Reset the registry (useful for testing)
   */
  reset(): void {
    this.providers.clear()
    this.initialized = false
  }
}

// Singleton instance
export const toolRegistry = new ToolRegistry()
